import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, FormGroup, FormControl, ControlLabel, Radio } from 'react-bootstrap';
// import DatePicker from 'react-datepicker';
// import "react-datepicker/dist/react-datepicker.css";
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';   
import { addDaysToDate, formatPhoneNumber } from '../utils/Common';
import { createPaymentUpdatePortal, getCustomerPaymentMethod, updateCustomerShipping } from '../utils/bedrockAPI';
import AddressDisplay from './AddressDisplay';
import styled from 'styled-components';
import './AddCameraModal.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const DesktopDateTimePicker = styled(DatePicker)`
    & .MuiInputBase-input {
        font-size: 18px;
        border: 1px solid black;
        padding: 5px;
        cursor: pointer;
        text-align: center;
        max-width: 240px;
        min-height: 40px;
        border-radius: 6px;
    }
    
    & .MuiInputBase-input:hover {
        background-color: #F5F5F5;
    }
  `;

const MobileDateTimePicker = styled(DatePicker)`
    & .MuiInputBase-input {
        font-size: 12px;
        border: 1px solid black;
        padding: 5px;
        cursor: pointer;
        text-align: center;
        max-width: 130px;
        min-height: 25px;
        border-radius: 3px;
    }
    
    & .MuiInputBase-input:hover {
        background-color: #F5F5F5;
    }
`;

export default function AddCameraModal({ show, onHide, stripeCustomer, builderId }) {
  const [step, setStep] = useState(1);
  const [deliveryDate, setDeliveryDate] = useState(addDaysToDate(new Date(), 5));
  const [billingOption, setBillingOption] = useState('existing');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('default');
  const [shippingAddress, setShippingAddress] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isMounted, setIsMounted] = useState(true);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editedAddress, setEditedAddress] = useState(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (stripeCustomer?.shipping) {
      const shippingName = stripeCustomer.shipping.name;
      const companyName = stripeCustomer.name || shippingName;
      const phone = formatPhoneNumber(stripeCustomer.phone);
      
      setShippingAddress({
        company: companyName !== shippingName ? companyName : null,
        name: shippingName,
        phone,
        street: stripeCustomer.shipping.address.line1,
        city: stripeCustomer.shipping.address.city,
        state: stripeCustomer.shipping.address.state,
        zip: stripeCustomer.shipping.address.postal_code
      });
    }
    loadPaymentMethod();
  }, [stripeCustomer]);

  useEffect(() => {
    if(step === 3) {
      loadPaymentMethod();
    }
  }, [step]);

  useEffect(() => {
    if (shippingAddress) {
      setEditedAddress({
        companyName: shippingAddress.company || '',
        shippingName: shippingAddress.name || '',
        shippingAddress: {
          line1: shippingAddress.street || '',
          line2: '',
          city: shippingAddress.city || '',
          state: shippingAddress.state || '',
          postal_code: shippingAddress.zip || '',
          country: 'US'
        },
        shippingPhone: shippingAddress.phone || ''
      });
    }
  }, [shippingAddress]);

  const loadPaymentMethod = async () => {
    const paymentMethod = await getCustomerPaymentMethod(builderId);
    console.log(paymentMethod);
    if(isMounted) {
      setPaymentMethod(paymentMethod);
    }
  }

  const handleClose = () => {
    setStep(1);
    // setDeliveryDate(new Date());
    setBillingOption('existing');
    setSelectedPaymentMethod('default');
    onHide();
  };

  const handleSubmit = async () => {
    // Placeholder API call
    // TODO TODO TODO
    console.log('Submitting new camera order:', {
      deliveryDate,
      billingOption,
      selectedPaymentMethod,
      shippingAddress
    });
    setStep(5);
  };

  const renderStep1 = () => (
    <div>
      <h4 className="modal-step-title">When do you need the camera system?</h4>
      <p className="modal-step-description">
        We will ship your camera system to arrive by this date. If there are any delays, we'll notify you immediately.
      </p>
      <div className="modal-step-content">
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          {isMobile ? (
            <MobileDateTimePicker
              value={deliveryDate}
              onChange={date => setDeliveryDate(date)}
              minDate={addDaysToDate(new Date(), 4)}
              InputProps={{disableUnderline:true}}
            />
          ) : (
            <DesktopDateTimePicker
              value={deliveryDate}
              onChange={date => setDeliveryDate(date)}
              minDate={addDaysToDate(new Date(), 4)}
              InputProps={{disableUnderline:true}}
            />
          )}
        </MuiPickersUtilsProvider>
      </div>
      <div className="modal-footer">
        <Button onClick={() => setStep(2)}>Next</Button>
      </div>
    </div>
  );

  // TODO TODO TODO - This needs invoice info to make this clear to the customer
  const renderStep2 = () => (
    <div>
      <h4 className="modal-step-title">How would you like to handle billing for this camera?</h4>
      <Form>
        <Radio
          name="billingOption"
          value="existing"
          checked={billingOption === 'existing'}
          onChange={() => setBillingOption('existing')}
          className="modal-radio"
        >
          Include with existing subscription
        </Radio>
        <Radio
          name="billingOption"
          value="new"
          checked={billingOption === 'new'}
          onChange={() => setBillingOption('new')}
          className="modal-radio"
        >
          Create new subscription with separate invoices
        </Radio>
      </Form>
      <div className="modal-footer">
        <Button onClick={() => setStep(1)}>Back</Button>
        <Button onClick={() => setStep(3)}>Next</Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h4 className="modal-step-title">How would you like to pay for this camera?</h4>
      <Form>
        <Radio
          name="paymentMethod"
          value="default"
          checked={selectedPaymentMethod === 'default'}
          onChange={() => setSelectedPaymentMethod('default')}
          className="modal-radio"
        >
          Use default card on file
        </Radio>
        {selectedPaymentMethod === 'default' && paymentMethod && (
        <div className="payment-method-display">
          <div className="card-icon">
            {paymentMethod.type === 'card' && (
              <>
                {paymentMethod.card.brand === 'visa' && <i className="fab fa-cc-visa"></i>}
                {paymentMethod.card.brand === 'mastercard' && <i className="fab fa-cc-mastercard"></i>}
                {paymentMethod.card.brand === 'amex' && <i className="fab fa-cc-amex"></i>}
                {paymentMethod.card.brand === 'discover' && <i className="fab fa-cc-discover"></i>}
                {!['visa', 'mastercard', 'amex', 'discover'].includes(paymentMethod.card.brand) && 
                  <i className="far fa-credit-card"></i>}
              </>
            )}
            {paymentMethod.type === 'us_bank_account' && <i className="fas fa-university"></i>}
            {!['card', 'us_bank_account'].includes(paymentMethod.type) && <i className="fas fa-money-bill-wave"></i>}
          </div>
          <div className="card-details">
            <div className="card-info">
              {paymentMethod.type === 'card' && (
                <>
                  <span className="card-brand">{paymentMethod.card.display_brand.replace('_', ' ').toUpperCase()}</span>
                  <span className="card-number">•••• {paymentMethod.card.last4}</span>
                </>
              )}
              {paymentMethod.type === 'us_bank_account' && (
                <>
                  <span className="card-brand">{paymentMethod.us_bank_account.bank_name}</span>
                  <span className="card-number">•••• {paymentMethod.us_bank_account.last4}</span>
                </>
              )}
              {!['card', 'us_bank_account'].includes(paymentMethod.type) && (
                <span className="card-brand">{paymentMethod.type.replace('_', ' ').toUpperCase()}</span>
              )}
            </div>
            {paymentMethod.type === 'card' && (
              <div className="card-expiry">Expires {paymentMethod.card.exp_month}/{paymentMethod.card.exp_year}</div>
            )}
            {paymentMethod.type === 'us_bank_account' && (
              <div className="card-expiry">{paymentMethod.us_bank_account.account_type.charAt(0).toUpperCase() + paymentMethod.us_bank_account.account_type.slice(1)} Account</div>
            )}
          </div>
        </div>
      )}
        <Button 
          variant="outline-primary" 
          className="update-payment-btn"
          onClick={async () => {
            try {
              const { url } = await createPaymentUpdatePortal(builderId);
              window.open(url, '_blank');
              setStep(2);
            } catch (error) {
              console.error('Error opening payment portal:', error);
              // You might want to show an error message to the user here
            }
          }}
        >
          Update Payment Method
        </Button>
      </Form>
      
      <div className="modal-footer">
        <Button onClick={() => setStep(2)}>Back</Button>
        <Button onClick={() => setStep(4)}>Next</Button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div>
      <h4 className="modal-step-title">Please confirm your shipping address</h4>
        <AddressDisplay
          address={shippingAddress}
          setShippingAddress={setShippingAddress}
          builderId={builderId}
          isEditingAddress={isEditingAddress}
          setIsEditingAddress={setIsEditingAddress}
        />
      <div className="modal-footer">
        <Button onClick={() => setStep(3)}>Back</Button>
        <Button onClick={handleSubmit} disabled={!shippingAddress || isEditingAddress}>Confirm Order</Button>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div>
      <h4 className="modal-step-title">Order Confirmation</h4>
      <p className="modal-step-description">We're processing your order and will send you a confirmation when it ships.</p>
      <div className="modal-footer">
        <Button onClick={handleClose}>Close</Button>
      </div>
    </div>
  );

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      size="lg" 
      className="camera-modal"
      enforceFocus={false}
      restoreFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Order New Camera</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </Modal.Body>
    </Modal>
  );
} 